import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Share2, Download, Copy, Shield, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const VERSES = [
  { text: 'See, I am doing a new thing! Now it springs up; do you not perceive it?', ref: 'Isaiah 43:19' },
  { text: 'The Lord is close to the brokenhearted and saves those who are crushed in spirit.', ref: 'Psalm 34:18' },
  { text: 'Commit to the Lord whatever you do, and he will establish your plans.', ref: 'Proverbs 16:3' },
  { text: 'For I know the plans I have for you — plans to prosper you and not to harm you.', ref: 'Jeremiah 29:11' },
  { text: 'The rich rule over the poor, and the borrower is slave to the lender.', ref: 'Proverbs 22:7' },
];

function pickVerse(seed = 0) {
  return VERSES[seed % VERSES.length];
}

export default function ShareProgressCard({ open, onOpenChange, stats, userName }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const verse = pickVerse((stats?.itemsCleared ?? 0) + (stats?.disputesFiled ?? 0));
  const firstName = (userName || 'Friend').split(' ')[0];

  const renderPng = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return canvas;
  };

  const handleDownload = async () => {
    try {
      setBusy(true);
      const canvas = await renderPng();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `selah-slate-progress-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Progress card downloaded');
    } catch (e) {
      toast.error('Could not download card');
    } finally {
      setBusy(false);
    }
  };

  const handleNativeShare = async () => {
    try {
      setBusy(true);
      const canvas = await renderPng();
      if (!canvas) return;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Could not create image');

      const file = new File([blob], 'selah-slate-progress.png', { type: 'image/png' });
      const shareData = {
        title: 'My Selah Slate Progress',
        text: `Clearing my slate, one dispute at a time. ${stats?.itemsCleared ?? 0} items cleared on my journey to financial freedom. Join me — it's free forever.`,
        url: window.location.origin,
      };

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: download
        handleDownload();
        return;
      }
      toast.success('Shared!');
    } catch (e) {
      if (e?.name !== 'AbortError') {
        toast.error('Sharing not supported — try downloading instead');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="share-progress-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] text-2xl text-[#36454F]">
            Share Your Progress
          </DialogTitle>
          <DialogDescription className="font-['Manrope'] text-[#36454F]/65">
            A shareable testimony card of your journey so far.
          </DialogDescription>
        </DialogHeader>

        {/* The card (rendered on-screen and captured for export) */}
        <div className="p-4 bg-[#F5F5F5] rounded-sm">
          <div
            ref={cardRef}
            className="relative overflow-hidden"
            style={{
              width: '100%',
              aspectRatio: '1 / 1',
              background: 'linear-gradient(135deg, #36454F 0%, #4a5a66 60%, #B76E79 160%)',
              padding: '32px',
              color: '#FFFFFF',
              fontFamily: "'Manrope', system-ui, sans-serif",
              borderRadius: '8px',
            }}
            data-testid="share-progress-card-image"
          >
            {/* Decorative shield */}
            <div style={{ position: 'absolute', top: -20, right: -20, opacity: 0.08 }}>
              <svg width="260" height="260" viewBox="0 0 64 64" fill="#FFFFFF">
                <path d="M32 4 L56 14 L56 30 C56 44 46 56 32 60 C18 56 8 44 8 30 L8 14 Z" />
              </svg>
            </div>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '8px',
                  background: '#B76E79',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 64 64" fill="#FFFFFF">
                  <path d="M32 6 L54 14 L54 30 C54 42 46 52 32 56 C18 52 10 42 10 30 L10 14 Z" />
                </svg>
              </div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: 600 }}>
                Selah Slate™
              </div>
            </div>

            {/* Name */}
            <div style={{ fontSize: '13px', opacity: 0.7, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {firstName}'s Journey
            </div>

            {/* Stats Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '14px',
                marginTop: '20px',
              }}
            >
              <StatTile label="Items Cleared" value={stats?.itemsCleared ?? 0} accent />
              <StatTile label="Disputes Filed" value={stats?.disputesFiled ?? 0} />
              <StatTile label="Bureaus Engaged" value={stats?.bureausEngaged ?? 0} />
              <StatTile label="Days on Journey" value={stats?.daysOnJourney ?? 0} />
            </div>

            {/* Verse */}
            <div
              style={{
                marginTop: '24px',
                padding: '14px 16px',
                background: 'rgba(255,255,255,0.09)',
                borderRadius: '6px',
                borderLeft: '3px solid #E0BFB8',
              }}
            >
              <div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: 'italic',
                  fontSize: '13px',
                  lineHeight: '1.45',
                }}
              >
                "{verse.text}"
              </div>
              <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '6px' }}>— {verse.ref}</div>
            </div>

            {/* Footer */}
            <div
              style={{
                position: 'absolute',
                bottom: '24px',
                left: '32px',
                right: '32px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
                opacity: 0.7,
              }}
            >
              <span>Free forever · selahslate.com</span>
              <span style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                Clear your credit. Reclaim your legacy.
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <Button
            onClick={handleNativeShare}
            disabled={busy}
            className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
            data-testid="share-native-btn"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Share
          </Button>
          <Button
            onClick={handleDownload}
            disabled={busy}
            variant="outline"
            className="border-[#36454F]/30 text-[#36454F] hover:bg-[#36454F] hover:text-white font-['Manrope'] rounded-sm"
            data-testid="share-download-btn"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Download PNG
          </Button>
          <Button
            onClick={handleCopyLink}
            disabled={busy}
            variant="outline"
            className="border-[#36454F]/30 text-[#36454F] hover:bg-[#36454F] hover:text-white font-['Manrope'] rounded-sm"
            data-testid="share-copy-link-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Copy Link
              </>
            )}
          </Button>
        </div>

        <p className="text-[11px] text-[#36454F]/50 font-['Manrope'] text-center mt-2">
          Your specific accounts and personal info are never included — only aggregate progress.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: accent ? 'rgba(183, 110, 121, 0.22)' : 'rgba(255, 255, 255, 0.09)',
        borderRadius: '6px',
        border: accent ? '1px solid rgba(224, 191, 184, 0.35)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div style={{ fontSize: '28px', fontWeight: 700, fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', opacity: 0.75, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
    </div>
  );
}
